import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, NotebookPen, Heart } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { FAMILY_COMMITMENTS, IMAGES } from "@/data/content";

const ICONS = [ShieldCheck, NotebookPen, Heart];

export const ForFamilies = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section
      id="for-families"
      ref={ref}
      data-testid="for-families-section"
      className="overflow-hidden bg-cream-soft py-24 lg:py-36"
    >
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 lg:grid-cols-2 lg:px-10">
        <Reveal className="relative order-2 lg:order-1">
          <div
            aria-hidden="true"
            className="absolute -inset-6 rounded-[3rem] bg-gradient-to-tr from-sage/25 to-mauve/20 blur-2xl"
          />
          <motion.div
            style={{ y: imageY }}
            className="relative overflow-hidden rounded-[2.5rem] shadow-spotlight ring-1 ring-navy/10"
          >
            <img
              src={IMAGES.gardenWalk}
              alt="Two companions walking gently side by side along a leafy garden path"
              className="aspect-[4/5] w-full object-cover sm:aspect-[5/5]"
              loading="lazy"
              decoding="async"
            />
          </motion.div>
          <div className="absolute -bottom-6 -right-2 rounded-2xl border border-navy/[0.06] bg-white/95 p-5 shadow-elevated backdrop-blur sm:-right-6">
            <p className="font-serif text-3xl text-navy">2 hrs</p>
            <p className="mt-1 text-xs font-medium text-charcoal/60">
              Minimum visit — always unhurried
            </p>
          </div>
        </Reveal>

        <div className="order-1 lg:order-2">
          <Reveal>
            <Eyebrow>For Families</Eyebrow>
            <h2 className="mt-4 font-serif text-2xl leading-tight tracking-tight text-navy sm:text-3xl lg:text-4xl">
              Guilt-free peace of mind for loving sons and daughters
            </h2>
            <p className="mt-5 text-base leading-relaxed text-charcoal/75">
              Balancing career, children, and caring for an ageing parent can be
              emotionally exhausting. Adeya provides the devoted, patient friend you
              wish you could be every single afternoon.
            </p>
          </Reveal>

          <ul className="mt-10 space-y-8">
            {FAMILY_COMMITMENTS.map((commitment, i) => {
              const Icon = ICONS[i];
              return (
                <Reveal key={commitment.title} delay={i * 0.1}>
                  <li data-testid={`family-commitment-${i + 1}`} className="flex gap-5">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-mauve-light text-mauve-dark">
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <div>
                      <h3 className="font-sans text-lg font-semibold text-navy">{commitment.title}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-charcoal/70">{commitment.text}</p>
                    </div>
                  </li>
                </Reveal>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
};
